import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn, ZoomOut, Eye, Printer, Download, Trash2, RotateCcw } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest } from '../../store/labStructures';
type CViewingTest = {
  kind: 'lab';
  labId: string;
  testName?: string;
} | {
  kind: 'rad';
  radId: string;
} | null;
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    logAudit
  } = useData();
  const [activeTab, setActiveTab] = useState<'queue' | 'completed'>('queue');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // View Test File modal state
  const [viewingTest, setViewingTest] = useState<CViewingTest>(null);
  const [testImageZoom, setTestImageZoom] = useState<number>(100);
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const testModalRef = useRef<HTMLDivElement>(null);
  // Remove Test Modal state
  const [removeTestModal, setRemoveTestModal] = useState<{
    isOpen: boolean;
    labId: string;
    testName: string;
    reason: string;
  }>({
    isOpen: false,
    labId: '',
    testName: '',
    reason: ''
  });
  const closeTestModal = () => {
    setViewingTest(null);
    setTestImageZoom(100);
  };
  const handlePrintTestModal = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    onExaminationDetails: {
      gc: '',
      febrile: '',
      pjc: '',
      s1s2: '',
      rs: '',
      pa: ''
    },
    significantFindings: [],
    workingDiagnosis: []
  });
  const [reviewDateNotes, setReviewDateNotes] = useState('');
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  // Active queue only. Completed visits live in Patient Files and can be
  // reopened from there via the "Amend File" flow.
  const pendingEncounters = encounters.filter(e => e.stage !== 'COMPLETED');
  const completedEncounters = encounters.filter(e => e.stage === 'COMPLETED');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const filteredCompleted = completedEncounters.filter(enc => {
    const p = getPatient(enc.patientId);
    if (!p) return false;
    const q = searchQuery.toLowerCase();
    return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    setReviewDateNotes(encounter.reviewDateNotes || '');
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        onExaminationDetails: {
          gc: '',
          febrile: '',
          pjc: '',
          s1s2: '',
          rs: '',
          pa: ''
        },
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN',
        lastAttendingClinician: currentUser?.fullName
      });
    } else if (currentUser && encounter.lastAttendingClinician !== currentUser.fullName) {
      // Record that this clinician opened/consulted the file.
      updateEncounter(encounter.id, {
        lastAttendingClinician: currentUser.fullName
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const isAmending = selectedEncounter.stage === 'COMPLETED';
    let editHistory = selectedEncounter.clinicalNote?.editHistory || [];
    if (isAmending) {
      const reason = prompt('Please enter a mandatory reason for modifying this completed consultation:');
      if (!reason || !reason.trim()) {
        alert('A reason is required to amend a completed consultation.');
        return;
      }
      const previousNote = selectedEncounter.clinicalNote!;
      const {
        editHistory: _oldHistory,
        ...noteSnapshot
      } = previousNote;
      editHistory = [...editHistory, {
        editedAt: new Date().toISOString(),
        editedBy: currentUser.id,
        editedByName: currentUser.fullName,
        reason: reason.trim(),
        previousNote: noteSnapshot as any
      }];
      logAudit({
        type: 'consultation.edit',
        encounterId: selectedEncounter.id,
        patientId: selectedEncounter.patientId,
        patientName: `${getPatient(selectedEncounter.patientId)?.firstName} ${getPatient(selectedEncounter.patientId)?.lastName}`,
        userId: currentUser.id,
        userName: currentUser.fullName,
        userRole: currentUser.role,
        reason: reason.trim(),
        previousContent: JSON.stringify(noteSnapshot),
        newContent: JSON.stringify(notes)
      });
    }
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: isAmending ? selectedEncounter.clinicalNote!.recordedAt : new Date().toISOString(),
      recordedBy: isAmending ? selectedEncounter.clinicalNote!.recordedBy : currentUser.id,
      clinicianName: isAmending ? selectedEncounter.clinicalNote!.clinicianName : currentUser.fullName,
      editHistory
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName
    });
    alert(isAmending ? 'Amendment saved and logged successfully.' : 'Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Laboratory tests requested. The patient file remains open — you can continue your consultation.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Radiology scan requested. The patient file remains open — you can continue your consultation.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    if (selectedEncounter.prescriptions.length >= 20) {
      alert('You have reached the maximum of 20 drugs per prescription. Remove an existing item before adding another.');
      return;
    }
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  // Delete a single prescription. Only allowed while it is still Pending —
  // once Pharmacy has dispensed (or marked unavailable), it stays on record.
  const handleDeletePrescription = (rxId: string) => {
    if (!selectedEncounter) return;
    const rx = selectedEncounter.prescriptions.find(p => p.id === rxId);
    if (!rx) return;
    if (rx.status !== 'Pending') {
      alert('This drug has already been processed by Pharmacy and cannot be removed from the record.');
      return;
    }
    if (!confirm(`Remove "${rx.drugName}" from the prescription list? This cannot be undone.`)) return;
    updateEncounter(selectedEncounter.id, {
      prescriptions: selectedEncounter.prescriptions.filter(p => p.id !== rxId)
    });
  };
  // Recall (remove) a single ordered lab test that has not yet been completed.
  // - For tests inside lab.tests[]: drop just that test from the array.
  // - If that empties the request (and no otherTests), remove the whole request.
  // - For an "other test" entry: clear lab.otherTests on that request, or
  //   remove the request entirely if it has no tests left either.
  const handleRecallLabTest = (labId: string, testName: string, isOther = false) => {
    if (!selectedEncounter) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === labId);
    if (!lab) return;
    if (lab.status === 'Completed') {
      alert('This test has already been completed by the laboratory and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the lab test "${testName}"? The laboratory will no longer see this request.`)) return;
    let updatedLabs = selectedEncounter.labRequests.map(l => {
      if (l.id !== labId) return l;
      if (isOther) return {
        ...l,
        otherTests: ''
      };
      return {
        ...l,
        tests: l.tests.filter(t => t !== testName)
      };
    });
    // Drop any request that now has no tests AND no otherTests.
    updatedLabs = updatedLabs.filter(l => l.tests.length > 0 || l.otherTests.trim().length > 0);
    const stillPendingLab = updatedLabs.some(l => l.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabs,
      pendingLab: stillPendingLab
    });
  };
  // Recall (remove) a pending radiology request.
  const handleRecallRadiology = (radId: string) => {
    if (!selectedEncounter) return;
    const rad = selectedEncounter.radiologyRequests.find(r => r.id === radId);
    if (!rad) return;
    if (rad.status === 'Completed') {
      alert('This scan has already been completed by radiology and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the radiology request "${rad.scanType}"? Radiology will no longer see this request.`)) return;
    const updatedRads = selectedEncounter.radiologyRequests.filter(r => r.id !== radId);
    const stillPendingRad = updatedRads.some(r => r.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: updatedRads,
      pendingRadiology: stillPendingRad
    });
  };
  const handleRemoveTestConfirm = () => {
    if (!selectedEncounter || !currentUser || !removeTestModal.reason.trim()) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === removeTestModal.labId);
    if (!lab) return;
    const now = new Date().toISOString();
    const newEntry = {
      testName: removeTestModal.testName,
      removedAt: now,
      removedBy: currentUser.id,
      reason: removeTestModal.reason
    };
    const updatedLabRequests = selectedEncounter.labRequests.map(l => {
      if (l.id !== removeTestModal.labId) return l;
      return {
        ...l,
        removedTestNames: [...(l.removedTestNames || []), removeTestModal.testName],
        removedTestEntries: [...(l.removedTestEntries || []), newEntry]
      };
    });
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabRequests
    });
    const patient = patients.find(p => p.id === selectedEncounter.patientId);
    logAudit({
      type: 'lab.test.remove',
      encounterId: selectedEncounter.id,
      patientId: selectedEncounter.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: removeTestModal.reason,
      labRequestId: removeTestModal.labId,
      testName: removeTestModal.testName
    });
    setRemoveTestModal({
      isOpen: false,
      labId: '',
      testName: '',
      reason: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    // File stays open — clinician decides when to close, save draft, or complete the visit.
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}). The patient file remains open — you can continue your consultation.`);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-1983">Patient not found</div>;
    const isCompleted = selectedEncounter.stage === 'COMPLETED';
    const isOriginalClinician = selectedEncounter.clinicalNote?.recordedBy === currentUser?.id;
    const isAdmin = currentUser?.role === 'Administrator';
    const canEdit = !isCompleted || isOriginalClinician || isAdmin;
    return <div className="space-y-6 pb-20" data-id="element-1984">
        {isCompleted && !canEdit && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-1985">
            <span className="text-sm font-medium" data-id="element-1986">
              This consultation is completed and can only be amended by the
              original clinician (
              {selectedEncounter.clinicalNote?.clinicianName}) or an
              Administrator.
            </span>
          </div>}
        {isCompleted && canEdit && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-1987">
            <History className="w-5 h-5 mr-2 text-amber-600" data-id="element-1988" />
            <span className="text-sm font-medium" data-id="element-1989">
              You are viewing a completed consultation. Any edits saved will be
              logged in the audit trail.
            </span>
          </div>}
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-1990">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1991">
            {isCompleted ? 'Completed Consultation' : 'Consultation'}
          </h1>
          <div className="flex space-x-2" data-id="element-1992">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-1993">
              Close File
            </Button>
            <Button variant="outline" className="text-amber-700 border-amber-600 hover:bg-amber-50" onClick={() => {
            const reason = prompt('Send this file back to Nursing so the vitals can be corrected. Please provide a brief reason (optional):', '') ?? '';
            if (confirm('Are you sure you want to send this file back to Nursing? The patient will leave your queue until the nurse re-submits the corrected vitals.')) {
              const notePrefix = reason ? `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}: ${reason}]` : `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}]`;
              updateEncounter(selectedEncounter.id, {
                stage: 'NURSING',
                vitalsReturnNote: notePrefix,
                vitalsReturnedAt: new Date().toISOString()
              } as any);
              alert('File sent back to Nursing for vitals correction. It will return to your queue once the nurse re-submits the vitals.');
              setSelectedEncounterId(null);
            }
          }} data-id="element-1994">
              ↩ Send to Nursing
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-1995">
              {selectedEncounter.stage === 'COMPLETED' ? 'Save Amendment' : 'Save Draft'}
            </Button>
            {selectedEncounter.stage !== 'COMPLETED' && <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-1996">
                <CheckCircle className="w-4 h-4 mr-2" data-id="element-1997" /> Complete Visit
              </Button>}
          </div>
        </div>

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-1998">
          <fieldset disabled={!canEdit} className="group" data-id="element-1999">
            {/* ═══ FORM HEADER ═══ */}
            {selectedEncounter.lastAttendingClinician && selectedEncounter.lastAttendingClinician !== currentUser?.fullName && <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded flex items-center" data-id="element-2000">
                  <Stethoscope className="w-5 h-5 mr-2 text-blue-600" data-id="element-2001" />
                  <span className="text-sm font-medium" data-id="element-2002">
                    This file was last attended to by:{' '}
                    <span className="font-bold" data-id="element-2003">
                      {selectedEncounter.lastAttendingClinician}
                    </span>
                  </span>
                </div>}
            <div className="flex justify-between items-start mb-6" data-id="element-2004">
              <div className="flex items-center" data-id="element-2005">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-2006" />
                <div data-id="element-2007">
                  <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-2008">
                    BMED MEDICAL CENTRE
                  </h1>
                  <p className="text-sm italic text-gray-600" data-id="element-2009">
                    Caring Healing Leading
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-2010">
                <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-2011">
                  Confidential
                </p>
                <p className="text-sm" data-id="element-2012">
                  <span className="font-semibold" data-id="element-2013">Date:</span>{' '}
                  {new Date(selectedEncounter.startedAt).toLocaleDateString()}
                </p>
                <p className="text-sm" data-id="element-2014">
                  <span className="font-semibold" data-id="element-2015">File No:</span>{' '}
                  {patient.bmcFileNumber}
                </p>
              </div>
            </div>

            {/* ═══ PERSONAL DETAILS ═══ */}
            <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-2016">
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2017">
                <div className="col-span-1 font-semibold" data-id="element-2018">1.</div>
                <div className="col-span-6" data-id="element-2019">
                  <span className="font-semibold" data-id="element-2020">Full Names:</span>{' '}
                  {patient.firstName} {patient.lastName}
                </div>
                <div className="col-span-3" data-id="element-2021">
                  <span className="font-semibold" data-id="element-2022">Sex:</span> {patient.sex}
                </div>
                <div className="col-span-2" data-id="element-2023">
                  <span className="font-semibold" data-id="element-2024">Age:</span> {patient.age}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2025">
                <div className="col-span-1 font-semibold" data-id="element-2026">2.</div>
                <div className="col-span-11" data-id="element-2027">
                  <span className="font-semibold" data-id="element-2028">Marital Status:</span>{' '}
                  {patient.maritalStatus}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2029">
                <div className="col-span-1 font-semibold" data-id="element-2030">3.</div>
                <div className="col-span-7" data-id="element-2031">
                  <span className="font-semibold" data-id="element-2032">Home Address:</span>{' '}
                  {patient.address}
                </div>
                <div className="col-span-4" data-id="element-2033">
                  <span className="font-semibold" data-id="element-2034">Contact:</span>{' '}
                  {patient.contact}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2035">
                <div className="col-span-1 font-semibold" data-id="element-2036">4.</div>
                <div className="col-span-11" data-id="element-2037">
                  <span className="font-semibold" data-id="element-2038">Occupation:</span>{' '}
                  {patient.occupation || '_________________________'}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2039">
                <div className="col-span-1 font-semibold" data-id="element-2040">5.</div>
                <div className="col-span-5" data-id="element-2041">
                  <span className="font-semibold" data-id="element-2042">Next of Kin:</span>{' '}
                  {patient.nextOfKin}
                </div>
                <div className="col-span-3" data-id="element-2043">
                  <span className="font-semibold" data-id="element-2044">Contact:</span>{' '}
                  {patient.nokContact}
                </div>
                <div className="col-span-3" data-id="element-2045">
                  <span className="font-semibold" data-id="element-2046">Relationship:</span>{' '}
                  {patient.nokRelationship}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2047">
                <div className="col-span-1 font-semibold" data-id="element-2048">6.</div>
                <div className="col-span-11" data-id="element-2049">
                  <span className="font-semibold" data-id="element-2050">Allergies:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2051">
                    {patient.allergies}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2" data-id="element-2052">
                <div className="col-span-1 font-semibold" data-id="element-2053">7.</div>
                <div className="col-span-11" data-id="element-2054">
                  <span className="font-semibold" data-id="element-2055">HIV Status:</span>{' '}
                  <span className="ml-4" data-id="element-2056">
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-2057">
                      Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-2058">
                      Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-2059">
                      Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* ═══ NURSING OBSERVATIONS ═══ */}
            <div className="mb-6" data-id="element-2060">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-2061">
                Nursing Observations
              </h3>
              <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-2062">
                <thead data-id="element-2063">
                  <tr className="bg-gray-50" data-id="element-2064">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-2065">
                        {n}
                      </th>)}
                  </tr>
                  <tr className="bg-gray-100" data-id="element-2066">
                    <th className="border border-black p-1" data-id="element-2067">Temp</th>
                    <th className="border border-black p-1" data-id="element-2068">Pulse</th>
                    <th className="border border-black p-1" data-id="element-2069">Resp</th>
                    <th className="border border-black p-1" data-id="element-2070">BP</th>
                    <th className="border border-black p-1" data-id="element-2071">SpO2</th>
                    <th className="border border-black p-1" data-id="element-2072">Weight (Kg)</th>
                    <th className="border border-black p-1" data-id="element-2073">Height</th>
                    <th className="border border-black p-1" data-id="element-2074">BMI</th>
                    <th className="border border-black p-1" data-id="element-2075">Pain Score</th>
                    <th className="border border-black p-1" data-id="element-2076">AVPU</th>
                  </tr>
                </thead>
                <tbody data-id="element-2077">
                  <tr data-id="element-2078">
                    <td className="border border-black p-2 font-medium" data-id="element-2079">
                      {selectedEncounter.vitals?.temp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2080">
                      {selectedEncounter.vitals?.pulse || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2081">
                      {selectedEncounter.vitals?.resp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2082">
                      {selectedEncounter.vitals?.bp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2083">
                      {selectedEncounter.vitals?.sp02 || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2084">
                      {selectedEncounter.vitals?.weight || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2085">
                      {selectedEncounter.vitals?.height || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2086">
                      {selectedEncounter.vitals?.bmi || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2087">
                      {selectedEncounter.vitals?.painScore || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2088">
                      {selectedEncounter.vitals?.avpu || ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
            {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-2089">
                  <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-2090">
                    ⚡ Stat Doses Administered by Nursing
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-2091">
                    <thead data-id="element-2092">
                      <tr className="bg-gray-100" data-id="element-2093">
                        <th className="border border-black p-1.5 w-10" data-id="element-2094">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2095">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2096">
                          Dose
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2097">
                          Route
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2098">
                          Time Given
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2099">
                          Nurse
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2100">
                      {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-2101">
                          <td className="border border-black p-1.5 text-center" data-id="element-2102">
                            {idx + 1}
                          </td>
                          <td className="border border-black p-1.5 font-medium" data-id="element-2103">
                            {dose.drugName}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2104">
                            {dose.dose}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2105">
                            {dose.route}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2106">
                            {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                          </td>
                          <td className="border border-black p-1.5 italic text-xs" data-id="element-2107">
                            {dose.nurseName || ''}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}

            {/* ═══ CLINICAL NOTES ═══ */}
            <div className="space-y-5 mb-6 text-sm" data-id="element-2108">
              {/* PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2109">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2110">
                  Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
                ...notes,
                presentingComplaint: e.target.value
              })} data-id="element-2111" />
              </div>

              {/* HISTORY OF PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2112">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2113">
                  History of Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
                ...notes,
                historyOfPresentingComplaint: e.target.value
              })} data-id="element-2114" />
              </div>

              {/* PAST MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2115">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2116">
                  Past Medical History / General Co-Morbidities
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2117">
                  <tbody data-id="element-2118">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => {
                    const checked = notes.pastMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2119">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2120">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2121">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2122">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.pastMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2123" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2124">
                            <input type="text" placeholder="Notes (optional)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.pastMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          pastMedicalHistoryNotes: {
                            ...(notes.pastMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2125" />
                          </td>
                        </tr>;
                  })}
                  </tbody>
                </table>
              </div>

              {/* DRUG HISTORY */}
              <div className="border-2 border-black" data-id="element-2126">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2127">
                  Drug History
                </h3>
                <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
                ...notes,
                drugHistory: e.target.value
              })} data-id="element-2128" />
                <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-2129">
                  <span className="font-semibold" data-id="element-2130">Any known allergy:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2131">
                    {patient.allergies}
                  </span>
                </div>
              </div>

              {/* FAMILY MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2132">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2133">
                  Family Medical History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2134">
                  <tbody data-id="element-2135">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => {
                    const checked = notes.familyMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2136">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2137">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2138">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2139">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2140" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2141">
                            <input type="text" placeholder="Notes (e.g. relative, severity)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryNotes: {
                            ...(notes.familyMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2142" />
                          </td>
                        </tr>;
                  })}
                    {(() => {
                    const otherChecked = notes.familyMedicalHistory?.includes('OTHER') || false;
                    return <tr className="border-b border-gray-300" data-id="element-2143">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2144">
                            OTHER
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2145">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2146">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={otherChecked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, 'OTHER']
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== 'OTHER')
                              });
                            }
                          }} data-id="element-2147" />
                              {otherChecked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2148">
                            <input type="text" placeholder="Specify (e.g. condition, relative)" disabled={!otherChecked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryOther || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryOther: e.target.value
                        })} data-id="element-2149" />
                          </td>
                        </tr>;
                  })()}
                  </tbody>
                </table>
              </div>

              {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
              <div className="border-2 border-black" data-id="element-2150">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2151">
                  Obstetrics and Gynaecology History
                </h3>
                <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
                ...notes,
                obgynHistory: e.target.value
              })} data-id="element-2152" />
              </div>

              {/* SOCIAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2153">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2154">
                  Social History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2155">
                  <tbody data-id="element-2156">
                    <tr className="border-b border-gray-300" data-id="element-2157">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2158">
                        Alcohol
                      </td>
                      <td className="p-2" data-id="element-2159">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          alcohol: e.target.value
                        }
                      })} data-id="element-2160" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2161">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2162">
                        Smoking
                      </td>
                      <td className="p-2" data-id="element-2163">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          smoking: e.target.value
                        }
                      })} data-id="element-2164" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2165">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2166">
                        Occupation
                      </td>
                      <td className="p-2" data-id="element-2167">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          occupation: e.target.value
                        }
                      })} data-id="element-2168" />
                      </td>
                    </tr>
                    <tr data-id="element-2169">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2170">
                        Mental Status
                      </td>
                      <td className="p-2" data-id="element-2171">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          mentalStatus: e.target.value
                        }
                      })} data-id="element-2172" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* REVIEW OF SYSTEMS */}
              <div className="border-2 border-black" data-id="element-2173">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2174">
                  Review of Systems
                </h3>
                <div className="p-3 space-y-2" data-id="element-2175">
                  {[{
                  key: 'rs',
                  label: 'R/S:'
                }, {
                  key: 'cvs',
                  label: 'CVS:'
                }, {
                  key: 'cns',
                  label: 'CNS:'
                }, {
                  key: 'git',
                  label: 'GIT:'
                }, {
                  key: 'gut',
                  label: 'GUT:'
                }, {
                  key: 'mss',
                  label: 'MSS:'
                }, {
                  key: 'is',
                  label: 'IS:'
                }].map(system => <div key={system.key} className="flex items-center" data-id="element-2176">
                      <span className="font-semibold w-16" data-id="element-2177">{system.label}</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                    ...notes,
                    reviewOfSystems: {
                      ...notes.reviewOfSystems,
                      [system.key]: e.target.value
                    }
                  })} data-id="element-2178" />
                    </div>)}
                </div>
              </div>

              {/* ON EXAMINATION */}
              <div className="border-2 border-black" data-id="element-2179">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2180">
                  On Examination
                </h3>
                <div className="p-4 space-y-3" data-id="element-2181">
                  {[{
                  key: 'gc' as const,
                  label: 'G/C',
                  helper: 'Stable / Ill Looking / Unconscious'
                }, {
                  key: 'febrile' as const,
                  label: 'Temperature',
                  helper: 'Febrile / Afebrile / Lethargic'
                }, {
                  key: 'pjc' as const,
                  label: 'P | J | C',
                  helper: 'Pallor / Jaundice / Cyanosis'
                }, {
                  key: 's1s2' as const,
                  label: 'S1  S2',
                  helper: 'Heart sounds'
                }, {
                  key: 'rs' as const,
                  label: 'R/S',
                  helper: 'Respiratory system'
                }, {
                  key: 'pa' as const,
                  label: 'P/A',
                  helper: 'Per abdomen'
                }].map(field => <div key={field.key} className="grid grid-cols-[7rem_1fr] gap-3 items-start" data-id="element-2182">
                      <div className="pt-2" data-id="element-2183">
                        <p className="font-bold text-sm text-gray-900" data-id="element-2184">
                          {field.label}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight" data-id="element-2185">
                          {field.helper}
                        </p>
                      </div>
                      <input type="text" value={notes.onExaminationDetails?.[field.key] || ''} onChange={e => setNotes({
                    ...notes,
                    onExaminationDetails: {
                      ...(notes.onExaminationDetails || {
                        gc: '',
                        febrile: '',
                        pjc: '',
                        s1s2: '',
                        rs: '',
                        pa: ''
                      }),
                      [field.key]: e.target.value
                    }
                  })} placeholder={`Enter ${field.label} findings`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" data-id="element-2186" />
                    </div>)}

                  {/* Additional notes (free text) */}
                  <div className="pt-3 border-t border-gray-200" data-id="element-2187">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-2188">
                      Additional Notes
                    </label>
                    <textarea className="w-full p-2 min-h-[80px] text-sm border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Any other examination findings..." value={notes.onExamination} onChange={e => setNotes({
                    ...notes,
                    onExamination: e.target.value
                  })} data-id="element-2189" />
                  </div>
                </div>
              </div>

              {/* SUMMARY OF SIGNIFICANT FINDINGS */}
              <div className="border-2 border-black" data-id="element-2190">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2191">
                  Summary of Significant Findings
                </h3>
                <div className="p-3 space-y-2" data-id="element-2192">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-2193">
                      <span className="font-medium mr-2 mt-1" data-id="element-2194">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                    const current = notes.significantFindings || Array(8).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      significantFindings: updated
                    });
                  }} data-id="element-2195" />
                    </div>)}
                </div>
              </div>

              {/* WORKING DIAGNOSIS */}
              <div className="border-2 border-black" data-id="element-2196">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2197">
                  Working Diagnosis
                </h3>
                <div className="p-3 space-y-2" data-id="element-2198">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-2199">
                      <span className="font-medium mr-2 mt-1" data-id="element-2200">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                    const current = notes.workingDiagnosis || Array(10).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      workingDiagnosis: updated
                    });
                  }} data-id="element-2201" />
                    </div>)}
                </div>
              </div>

              {/* INVESTIGATIONS TO BE DONE */}
              <div className="border-2 border-black" data-id="element-2202">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2203">
                  Investigations To Be Done
                </h3>
                <div className="p-4" data-id="element-2204">
                  {/* LABORATORY TESTS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2205">
                    Laboratory Tests
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2206">
                    <tbody data-id="element-2207">
                      {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2208">
                          {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-2209">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2210"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2211"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-2212">
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2213">
                                  {test}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2214">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2215">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* RADIOLOGY SCANS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2216">
                    Radiology Scans
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2217">
                    <tbody data-id="element-2218">
                      {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2219">
                          {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-2220">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2221"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2222"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a) || b.includes('cxr') && a.includes('cxr') || b.includes('hsg') && a.includes('hsg');
                        });
                        return <Fragment key={scan} data-id="element-2223">
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2224">
                                  {scan}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2225">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2226">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* ─── PENDING INVESTIGATIONS — RECALL ─── */}
                  {(() => {
                  const pendingLabs = selectedEncounter.labRequests.filter(l => l.status === 'Pending');
                  const pendingRads = selectedEncounter.radiologyRequests.filter(r => r.status === 'Pending');
                  const hasAny = pendingLabs.some(l => l.tests.length > 0 || l.otherTests.trim().length) || pendingRads.length > 0;
                  if (!hasAny) return null;
                  return <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3" data-id="element-2227">
                        <h4 className="font-bold text-xs uppercase text-amber-800 mb-2" data-id="element-2228">
                          Pending Investigations (Recall to make changes)
                        </h4>
                        <ul className="space-y-1.5 text-sm" data-id="element-2229">
                          {pendingLabs.flatMap(lab => lab.tests.map(testName => <li key={`${lab.id}-${testName}`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2230">
                                <span data-id="element-2231">
                                  <span className="font-medium" data-id="element-2232">
                                    {testName}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2233">
                                    · Laboratory ·{' '}
                                    {new Date(lab.requestedAt).toLocaleTimeString()}
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, testName)} data-id="element-2234">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2235" /> Recall
                                </Button>
                              </li>))}
                          {pendingLabs.filter(l => l.otherTests.trim().length > 0).map(lab => <li key={`${lab.id}-other`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2236">
                                <span data-id="element-2237">
                                  <span className="font-medium" data-id="element-2238">
                                    {lab.otherTests}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2239">
                                    · Other lab test
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, lab.otherTests, true)} data-id="element-2240">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2241" /> Recall
                                </Button>
                              </li>)}
                          {pendingRads.map(rad => <li key={rad.id} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2242">
                              <span data-id="element-2243">
                                <span className="font-medium" data-id="element-2244">
                                  {rad.scanType}
                                </span>{' '}
                                <span className="text-xs text-gray-500" data-id="element-2245">
                                  · Radiology ·{' '}
                                  {new Date(rad.requestedAt).toLocaleTimeString()}
                                </span>
                              </span>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallRadiology(rad.id)} data-id="element-2246">
                                <RotateCcw className="w-3 h-3 mr-1" data-id="element-2247" /> Recall
                              </Button>
                            </li>)}
                        </ul>
                        <p className="text-xs text-amber-700 mt-2 italic" data-id="element-2248">
                          Recall removes the request before the lab or radiology
                          department processes it. Once a test is completed, it
                          becomes part of the permanent record.
                        </p>
                      </div>;
                })()}

                  <div className="flex gap-3 mb-4" data-id="element-2249">
                    <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-2250">
                      <FlaskConical className="w-4 h-4 mr-2" data-id="element-2251" /> Request
                      Laboratory Test
                    </Button>
                    <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-2252">
                      <Scan className="w-4 h-4 mr-2" data-id="element-2253" /> Request Radiology Scan
                    </Button>
                  </div>

                  <div className="border-t border-gray-300 pt-2" data-id="element-2254">
                    <span className="font-semibold" data-id="element-2255">Other investigations:</span>{' '}
                    {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                  </div>

                  {/* Investigation Results — View File per test */}
                  {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-2256">
                      <h4 className="font-bold mb-3 uppercase text-center" data-id="element-2257">
                        Investigation Results
                      </h4>
                      <table className="w-full border-collapse text-sm" data-id="element-2258">
                        <thead data-id="element-2259">
                          <tr className="bg-gray-100" data-id="element-2260">
                            <th className="border border-black p-1.5 text-left" data-id="element-2261">
                              Test / Scan
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2262">
                              Type
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2263">
                              Performed By
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2264">
                              Completed
                            </th>
                            <th className="border border-black p-1.5 text-center w-32" data-id="element-2265">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-2266">
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-2267">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-2268">
                                      {testName}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2269">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2270">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2271">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center" data-id="element-2272">
                                      <div className="flex justify-center gap-2" data-id="element-2273">
                                        <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName
                            })} data-id="element-2274">
                                          <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2275" />{' '}
                                          View File
                                        </Button>
                                        <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2276">
                                          <Trash2 className="w-4 h-4" data-id="element-2277" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>))}
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.otherTests && !(l.removedTestNames || []).includes(l.otherTests)).map(lab => <tr key={`${lab.id}-other`} data-id="element-2278">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2279">
                                  {lab.otherTests}{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2280">
                                    (Other)
                                  </span>
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2281">
                                  Laboratory
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2282">—</td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2283">
                                  {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2284">
                                  <div className="flex justify-center gap-2" data-id="element-2285">
                                    <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName: lab.otherTests
                            })} data-id="element-2286">
                                      <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2287" /> View
                                      File
                                    </Button>
                                    <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName: lab.otherTests,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2288">
                                      <Trash2 className="w-4 h-4" data-id="element-2289" />
                                    </button>
                                  </div>
                                </td>
                              </tr>)}
                          {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-2290">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2291">
                                  {rad.scanType}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2292">
                                  Radiology
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2293">
                                  {rad.radiographerName || '—'}
                                </td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2294">
                                  {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2295">
                                  <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            radId: rad.id
                          })} data-id="element-2296">
                                    <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2297" /> View
                                    File
                                  </Button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>

              {/* DRUG TREATMENT / PRESCRIPTION */}
              <div className="border-2 border-black" data-id="element-2298">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2299">
                  Drug Treatment / Prescription
                  {selectedEncounter.prescriptions.length > 0 && <span className="ml-2 text-xs font-normal text-gray-600 lowercase" data-id="element-2300">
                      ({selectedEncounter.prescriptions.length} of 20 drugs)
                    </span>}
                </h3>
                <div className="max-h-[420px] overflow-y-auto" data-id="element-2301">
                  <table className="w-full border-collapse text-sm text-center" data-id="element-2302">
                    <thead data-id="element-2303">
                      <tr className="bg-gray-100" data-id="element-2304">
                        <th className="border border-black p-1.5 w-10" data-id="element-2305">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2306">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2307">Dose</th>
                        <th className="border border-black p-1.5" data-id="element-2308">Route</th>
                        <th className="border border-black p-1.5" data-id="element-2309">Frequency</th>
                        <th className="border border-black p-1.5" data-id="element-2310">
                          No. of Days
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2311">Signature</th>
                        <th className="border border-black p-1.5 w-16" data-id="element-2312">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2313">
                      {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Not Dispensed' ? 'bg-red-50' : ''} data-id="element-2314">
                              <td className="border border-black p-1.5" data-id="element-2315">
                                {i + 1}
                              </td>
                              <td className="border border-black p-1.5 text-left font-medium" data-id="element-2316">
                                {rx.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2317">
                                {rx.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2318">
                                {rx.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2319">
                                {rx.frequency}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2320">
                                {rx.days}
                              </td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-2321">
                                {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-2322">
                                    ✓ Dispensed
                                  </span>}
                                {rx.status === 'Not Dispensed' && <span className="text-red-700 font-bold" data-id="element-2323">
                                    ✗ Not Available
                                  </span>}
                                {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-2324">
                                    Pending
                                  </span>}
                                {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-2325">
                                    {rx.pharmacistName}
                                  </div>}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2326">
                                {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2327">
                                    <Trash2 className="w-4 h-4" data-id="element-2328" />
                                  </button> : <span className="text-gray-300" data-id="element-2329">—</span>}
                              </td>
                            </tr>) : Array.from({
                      length: 20
                    }, (_, idx) => idx + 1).map(i => <tr key={i} data-id="element-2330">
                              <td className="border border-black p-3" data-id="element-2331">{i}</td>
                              <td className="border border-black p-3" data-id="element-2332"></td>
                              <td className="border border-black p-3" data-id="element-2333"></td>
                              <td className="border border-black p-3" data-id="element-2334"></td>
                              <td className="border border-black p-3" data-id="element-2335"></td>
                              <td className="border border-black p-3" data-id="element-2336"></td>
                              <td className="border border-black p-3" data-id="element-2337"></td>
                              <td className="border border-black p-3" data-id="element-2338"></td>
                            </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-2339">
                  <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" disabled={selectedEncounter.prescriptions.length >= 20} data-id="element-2340">
                    <Pill className="w-4 h-4 mr-2" data-id="element-2341" />
                    {selectedEncounter.prescriptions.length >= 20 ? 'Maximum 20 Drugs Reached' : 'Prescribe Medication'}
                  </Button>
                  {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-2342">
                      <Pill className="w-4 h-4 mr-2" data-id="element-2343" /> Send Prescription to
                      Pharmacy
                    </Button>}
                </div>
              </div>

              {/* FOLLOW UP / REVIEW DATE */}
              <div className="border-2 border-black" data-id="element-2344">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2345">
                  Next Follow Up / Review Date (If Any)
                </h3>
                <table className="w-full border-collapse text-sm text-center" data-id="element-2346">
                  <thead data-id="element-2347">
                    <tr className="bg-gray-100" data-id="element-2348">
                      <th className="border border-black p-1 w-10" data-id="element-2349">No.</th>
                      <th className="border border-black p-1" data-id="element-2350">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2351">No.</th>
                      <th className="border border-black p-1" data-id="element-2352">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2353">No.</th>
                      <th className="border border-black p-1" data-id="element-2354">Date</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2355">
                    {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-2356">
                        <td className="border border-black p-1 font-medium" data-id="element-2357">
                          {row + 1}
                        </td>
                        <td className="border border-black p-1" data-id="element-2358">
                          {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                        if (e.target.value) {
                          updateEncounter(selectedEncounter.id, {
                            reviewDate: new Date(e.target.value).toISOString()
                          });
                        }
                      }} data-id="element-2359" /> : ''}
                        </td>
                        <td className="border border-black p-1 font-medium" data-id="element-2360">
                          {row + 6}
                        </td>
                        <td className="border border-black p-2" data-id="element-2361"></td>
                        <td className="border border-black p-1 font-medium" data-id="element-2362">
                          {row + 11}
                        </td>
                        <td className="border border-black p-2" data-id="element-2363"></td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="border-t border-black p-3" data-id="element-2364">
                  <h4 className="font-bold uppercase text-sm mb-2" data-id="element-2365">Notes</h4>
                  <textarea className="w-full p-2 border border-gray-300 rounded text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Add any notes related to the follow-up or review (e.g. reason for review, instructions to patient, expected investigations)…" value={reviewDateNotes} onChange={e => setReviewDateNotes(e.target.value)} data-id="element-2366" />
                </div>
              </div>

              {/* NAME OF MEDICAL PRACTITIONER */}
              <div className="border-2 border-black p-4 mt-6" data-id="element-2367">
                <p className="font-bold text-sm" data-id="element-2368">
                  Name of the Medical Practitioner:{' '}
                  <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-2369">
                    {selectedEncounter.clinicalNote?.clinicianName || currentUser?.fullName || ''}
                  </span>
                </p>
              </div>
            </div>
          </fieldset>

          {/* VERSION HISTORY VIEWER */}
          {selectedEncounter.clinicalNote?.editHistory && selectedEncounter.clinicalNote.editHistory.length > 0 && <div className="mt-8 border-t-2 border-dashed border-gray-300 pt-6 print:hidden" data-id="element-2370">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-2371">
                  <History className="w-5 h-5 mr-2 text-gray-500" data-id="element-2372" />
                  Version History
                </h3>
                <div className="space-y-4" data-id="element-2373">
                  {selectedEncounter.clinicalNote.editHistory.map((edit, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2374">
                        <div className="flex justify-between items-start mb-2" data-id="element-2375">
                          <div data-id="element-2376">
                            <p className="font-semibold text-gray-900" data-id="element-2377">
                              Edited by {edit.editedByName}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2378">
                              {new Date(edit.editedAt).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="warning" data-id="element-2379">Version {idx + 1}</Badge>
                        </div>
                        <div className="bg-white border border-gray-100 p-3 rounded text-sm text-gray-700 mt-2" data-id="element-2380">
                          <span className="font-semibold text-gray-900" data-id="element-2381">
                            Reason for change:
                          </span>{' '}
                          {edit.reason}
                        </div>
                        <details className="mt-3" data-id="element-2382">
                          <summary className="text-sm text-blue-600 cursor-pointer hover:underline font-medium" data-id="element-2383">
                            View previous note content
                          </summary>
                          <div className="mt-2 p-3 bg-white border border-gray-200 rounded text-xs text-gray-600 max-h-60 overflow-y-auto font-mono whitespace-pre-wrap" data-id="element-2384">
                            {JSON.stringify(edit.previousNote, null, 2)}
                          </div>
                        </details>
                      </div>)}
                </div>
              </div>}
        </div>

        {/* ═══ MODALS ═══ */}
        {removeTestModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2385">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2386">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-2387">
                <Trash2 className="w-5 h-5 mr-2" data-id="element-2388" /> Remove Duplicate Test
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2389">
                Why are you removing "{removeTestModal.testName}"? This action
                will be logged in the audit trail.
              </p>
              <div className="mb-4" data-id="element-2390">
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={removeTestModal.reason} onChange={e => setRemoveTestModal({
              ...removeTestModal,
              reason: e.target.value
            })} placeholder="e.g., Duplicate entry, entered by mistake..." data-id="element-2391" />
              </div>
              <div className="flex justify-end space-x-3" data-id="element-2392">
                <Button variant="outline" onClick={() => setRemoveTestModal({
              isOpen: false,
              labId: '',
              testName: '',
              reason: ''
            })} data-id="element-2393">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleRemoveTestConfirm} disabled={!removeTestModal.reason.trim()} data-id="element-2394">
                  Confirm Removal
                </Button>
              </div>
            </div>
          </div>}

        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2395">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-2396">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2397">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2398">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-2399">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2400" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-2401">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-2402">
                  <div data-id="element-2403">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2404">
                      HAEMATOLOGY
                    </h4>
                    {['FBC', 'Blood Group', 'CD4 Count', 'MPS/RDT', 'AAFB', 'Viral Load'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2405">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2406" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2407">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2408">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'U&E', 'FBS', 'RBS', 'Liver Function Test', 'VBG', 'ABG'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2409">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2410" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2411">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2412">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'RPR', 'HBs Ag', 'HCV', 'Hepatitis Profile', 'Gravindex (Pregnancy Test)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2413">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2414" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2415">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2416">
                      ENDOCRINOLOGY
                    </h4>
                    {['Hormonal Profile', 'Testosterone', 'Anti-Müllerian Hormone (AMH)', 'Beta HCG (Quantitative)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2417">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2418" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2419">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2420">
                      URINE / FERTILITY
                    </h4>
                    {['Urinalysis', 'Urine Microscopy', 'Urine Culture', 'Sperm Analysis', 'Sperm Culture', 'PAP Smear'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2421">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2422" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2423">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2424">
                      MICROBIOLOGY / SWABS
                    </h4>
                    {['HVS', 'Urethral Swab', 'Stool Microscopy'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2425">
                          <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2426" />{' '}
                          {test}
                        </label>)}
                  </div>
                  <div data-id="element-2427">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2428">
                      OTHER
                    </h4>
                    {['DOA 6 (Drugs of Abuse)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2429">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2430" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-2431">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-2432" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2433">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-2434">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-2435">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2436">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-2437">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2438">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2439">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-2440">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2441" />
                </button>
              </div>
              <div className="p-6" data-id="element-2442">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2443">
                  Scan Type <span className="text-red-500" data-id="element-2444">*</span>
                </label>
                <select required value={scanType} onChange={e => setScanType(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm" data-id="element-2445">
                  <option value="" data-id="element-2446">— Select a scan —</option>
                  <option value="Pelvic Scan" data-id="element-2447">Pelvic Scan</option>
                  <option value="Anomaly Scan" data-id="element-2448">Anomaly Scan</option>
                  <option value="OBS Scan" data-id="element-2449">OBS Scan</option>
                  <option value="Abdominal Scan" data-id="element-2450">Abdominal Scan</option>
                  <option value="Abdomen/Pelvis Scan" data-id="element-2451">
                    Abdomen and Pelvic Scan (Abdomen/Pelvis Scan)
                  </option>
                  <option value="CXR (Chest X-Ray)" data-id="element-2452">CXR (Chest X-Ray)</option>
                  <option value="HSG (Hysterosalpingography)" data-id="element-2453">
                    HSG (Hysterosalpingography)
                  </option>
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2454">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-2455">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-2456">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-2457">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4" data-id="element-2458">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-2459">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-2460">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2461">
                    Prescribe Medication
                  </h3>
                  <button onClick={() => setShowRxModal(false)} data-id="element-2462">
                    <X className="w-5 h-5 text-gray-400" data-id="element-2463" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1" data-id="element-2464">
                  <div className="grid grid-cols-2 gap-4" data-id="element-2465">
                    <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                  ...rxForm,
                  drugName: e.target.value
                })} required data-id="element-2466" />
                    <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                  ...rxForm,
                  dose: e.target.value
                })} required data-id="element-2467" />
                    <Select label="Route" options={[{
                  value: 'PO',
                  label: 'Oral (PO)'
                }, {
                  value: 'IV',
                  label: 'Intravenous (IV)'
                }, {
                  value: 'IM',
                  label: 'Intramuscular (IM)'
                }, {
                  value: 'Topical',
                  label: 'Topical'
                }, {
                  value: 'Vaginal',
                  label: 'Vaginal'
                }, {
                  value: 'Rectal',
                  label: 'Rectal'
                }]} value={rxForm.route} onChange={e => setRxForm({
                  ...rxForm,
                  route: e.target.value
                })} data-id="element-2468" />
                    <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                  ...rxForm,
                  frequency: e.target.value
                })} required data-id="element-2469" />
                    <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                  ...rxForm,
                  days: e.target.value
                })} required data-id="element-2470" />
                  </div>
                  <div className="mt-4 flex justify-end" data-id="element-2471">
                    <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-2472">
                      + Add to Prescription List
                    </Button>
                  </div>

                  {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-2473">
                      <div className="flex justify-between items-center mb-2" data-id="element-2474">
                        <h4 className="font-medium text-sm" data-id="element-2475">
                          Current List ({selectedEncounter.prescriptions.length}{' '}
                          of 20)
                        </h4>
                        {selectedEncounter.prescriptions.length >= 20 && <span className="text-xs text-amber-600 font-medium" data-id="element-2476">
                            Maximum reached
                          </span>}
                      </div>
                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1 border border-gray-200 rounded p-2 bg-gray-50" data-id="element-2477">
                        {selectedEncounter.prescriptions.map((rx, idx) => <li key={rx.id} className="text-sm bg-white p-2 rounded flex justify-between items-center border border-gray-100 gap-2" data-id="element-2478">
                            <span className="flex-1 min-w-0 truncate" data-id="element-2479">
                              <span className="text-gray-400 mr-2" data-id="element-2480">
                                {idx + 1}.
                              </span>
                              {rx.drugName} - {rx.dose}
                            </span>
                            <span className="text-gray-500 text-xs whitespace-nowrap" data-id="element-2481">
                              {rx.frequency} x {rx.days} days
                            </span>
                            {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors flex-shrink-0" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2482">
                                <Trash2 className="w-4 h-4" data-id="element-2483" />
                              </button> : <span className="text-xs text-gray-400 flex-shrink-0 italic" data-id="element-2484">
                                {rx.status}
                              </span>}
                          </li>)}
                      </ul>
                    </div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 flex-shrink-0 sticky bottom-0 rounded-b-lg z-10" data-id="element-2485">
                  <Button onClick={() => setShowRxModal(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-id="element-2486">
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-2487">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-2488">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-2489">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-2490">
                <X className="w-6 h-6" data-id="element-2491" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-2492">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-2493" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-2494">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}

        {/* ─── TEST FILE MODAL (View File per investigation) ─── */}
        {viewingTest && selectedEncounter && (() => {
        const patient = getPatient(selectedEncounter.patientId);
        if (!patient) return null;
        const lab = viewingTest.kind === 'lab' ? selectedEncounter.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? selectedEncounter.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        const focusedTest = viewingTest.kind === 'lab' ? viewingTest.testName : null;
        // Filter lab.results by the requested test name when possible
        const labResultsFiltered = lab && lab.results ? focusedTest ? Object.fromEntries(Object.entries(lab.results).filter(([k]) => k.toLowerCase().includes(focusedTest.toLowerCase()) || focusedTest.toLowerCase().includes(k.toLowerCase()) ||
        // FBC sub-tests, urinalysis (UA_), testosterone — keep all if focused test is one of these umbrella tests
        ['fbc', 'urinalysis', 'testosterone', 'sperm analysis'].some(u => focusedTest.toLowerCase().includes(u)))) : lab.results : null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-2495">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-2496">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-2497">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-2498">
                      {lab ? `Laboratory Result — ${focusedTest || lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}` : `Radiology Result — ${rad?.scanType}`}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-2499">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-2500">
                        <Printer className="w-4 h-4 mr-1" data-id="element-2501" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-2502">
                        <Download className="w-4 h-4 mr-1" data-id="element-2503" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-2504">
                        <X className="w-5 h-5 text-gray-500" data-id="element-2505" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-2506">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-2507">
                      <div className="flex items-center" data-id="element-2508">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-2509" />
                        <div data-id="element-2510">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-2511">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-2512">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-2513">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-2514">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-2515">
                          <span className="font-semibold" data-id="element-2516">Patient:</span>{' '}
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p data-id="element-2517">
                          <span className="font-semibold" data-id="element-2518">File No:</span>{' '}
                          {patient.bmcFileNumber}
                        </p>
                        <p data-id="element-2519">
                          <span className="font-semibold" data-id="element-2520">Age/Sex:</span>{' '}
                          {patient.age} / {patient.sex}
                        </p>
                        <p data-id="element-2521">
                          <span className="font-semibold" data-id="element-2522">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-2523">
                            <span className="font-semibold" data-id="element-2524">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && <div data-id="element-2525">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2526">
                          {focusedTest || 'Tests Performed'}
                        </h2>

                        {/* ─── Structured FBC Form ─── */}
                        {focusedTest && isFBCTest(focusedTest) ? <div className="space-y-4" data-id="element-2527">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2528">
                              <h3 className="font-bold uppercase" data-id="element-2529">
                                Full Blood Count (FBC) Report
                              </h3>
                            </div>
                            {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-2530">
                                <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-2531">
                                  {cat.category}
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2532">
                                  <thead data-id="element-2533">
                                    <tr className="bg-gray-50" data-id="element-2534">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2535">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2536">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2537">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-24" data-id="element-2538">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2539">
                                    {cat.tests.map(t => <tr key={t.name} data-id="element-2540">
                                        <td className="border border-black p-1.5" data-id="element-2541">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-2542">
                                          {lab.results && lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2543">
                                          {t.range}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2544">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div>)}
                          </div> : focusedTest && isUrinalysisTest(focusedTest) ? <div data-id="element-2545">
                            <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-2546">
                              <h3 className="font-bold uppercase" data-id="element-2547">
                                Urinalysis Report
                              </h3>
                            </div>
                            <div className="max-w-md mx-auto space-y-2" data-id="element-2548">
                              {urinalysisTests.map(t => {
                      const key = `UA_${t.name}`;
                      const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                      return <div key={t.name} className="flex items-center text-sm" data-id="element-2549">
                                    <span className="w-16 font-bold" data-id="element-2550">
                                      {t.name}
                                    </span>
                                    <span className="mx-3 text-gray-400" data-id="element-2551">
                                      -
                                    </span>
                                    <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-2552">
                                      {value || '\u00A0'}
                                    </span>
                                  </div>;
                    })}
                            </div>
                            <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-2553">
                              <div data-id="element-2554">
                                <p className="font-semibold mb-2" data-id="element-2555">
                                  Dr.'s Name:
                                </p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2556">
                                  {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                </p>
                              </div>
                              <div data-id="element-2557">
                                <p className="font-semibold mb-2" data-id="element-2558">Signature:</p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2559">
                                  {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                </p>
                              </div>
                            </div>
                          </div> : focusedTest && isTestosteroneTest(focusedTest) ? <div className="space-y-2" data-id="element-2560">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2561">
                              <h3 className="font-bold uppercase" data-id="element-2562">
                                Testosterone Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2563">
                              <thead data-id="element-2564">
                                <tr className="bg-gray-100" data-id="element-2565">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2566">
                                    Test
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2567">
                                    Result
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2568">
                                    Reference (Male)
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2569">
                                    Reference (Female)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2570">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2571">
                                {testosteroneTests.map(t => <tr key={t.name} data-id="element-2572">
                                    <td className="border border-black p-1.5" data-id="element-2573">
                                      {t.name}
                                    </td>
                                    <td className="border border-black p-1.5 font-bold" data-id="element-2574">
                                      {lab.results && (lab.results[`TESTO_${t.name}`] || lab.results[t.name]) || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2575">
                                      {t.rangeMale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2576">
                                      {t.rangeFemale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2577">
                                      {t.unit}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isHormonalProfileTest(focusedTest) ? <div data-id="element-2578">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2579">
                              <h3 className="font-bold uppercase" data-id="element-2580">
                                Hormonal Profile Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2581">
                              <thead data-id="element-2582">
                                <tr className="bg-gray-100" data-id="element-2583">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2584">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2585">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2586">
                                    Reference Range
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2587">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2588">
                                {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-2589">
                                    <tr className="align-top" data-id="element-2590">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-2591">
                                        {h.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-2592">
                                        {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2593">
                                        {h.ranges.map(r => <div key={r.label} data-id="element-2594">
                                            <span className="font-medium" data-id="element-2595">
                                              {r.label}:
                                            </span>{' '}
                                            {r.value}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2596">
                                        {h.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-2597">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2598">
                                        Method: {h.method}
                                      </td>
                                    </tr>
                                  </Fragment>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isAMHTest(focusedTest) ? <div data-id="element-2599">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2600">
                              <h3 className="font-bold uppercase" data-id="element-2601">
                                Anti Mullerian Hormone (AMH) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2602">
                              <thead data-id="element-2603">
                                <tr className="bg-gray-100" data-id="element-2604">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2605">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2606">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2607">
                                    Reference Range (by age, ng/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2608">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2609">
                                <tr className="align-top" data-id="element-2610">
                                  <td className="border border-black p-1.5 font-medium" data-id="element-2611">
                                    {amhStructure.name}
                                  </td>
                                  <td className="border border-black p-1.5 font-bold" data-id="element-2612">
                                    {lab.results && (lab.results[amhStructure.storageKey] || lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2613">
                                    {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-2614">
                                        <span className="font-medium" data-id="element-2615">
                                          {a.ageBand}:
                                        </span>{' '}
                                        {a.range}
                                      </div>)}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2616">
                                    {amhStructure.unit}
                                  </td>
                                </tr>
                                <tr data-id="element-2617">
                                  <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2618">
                                    Method: {amhStructure.method}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-2619">
                              <p className="font-bold uppercase mb-1" data-id="element-2620">
                                Interpretation
                              </p>
                              <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-2621">
                                {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-2622">{line}</li>)}
                              </ul>
                            </div>
                          </div> : focusedTest && isHCGTest(focusedTest) ? <div data-id="element-2623">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2624">
                              <h3 className="font-bold uppercase" data-id="element-2625">
                                Beta HCG (Quantitative) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2626">
                              <thead data-id="element-2627">
                                <tr className="bg-gray-100" data-id="element-2628">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2629">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2630">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2631">
                                    Reference Range (mIU/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2632">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2633">
                                {(() => {
                        const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                        const abnormal = isHCGAbnormal(hcgValue);
                        return <>
                                      <tr className="align-top" data-id="element-2634">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-2635">
                                          {hcgStructure.name}
                                        </td>
                                        <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-2636">
                                          {hcgValue || '—'}
                                          {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-2637">
                                              Above normal threshold (&gt; 6
                                              mIU/mL)
                                            </div>}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2638">
                                          {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-2639">
                                              <span className="font-medium" data-id="element-2640">
                                                {r.label}:
                                              </span>{' '}
                                              {r.range}
                                            </div>)}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2641">
                                          {hcgStructure.unit}
                                        </td>
                                      </tr>
                                      <tr data-id="element-2642">
                                        <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2643">
                                          Method: {hcgStructure.method}
                                        </td>
                                      </tr>
                                    </>;
                      })()}
                              </tbody>
                            </table>
                          </div> : labResultsFiltered && Object.keys(labResultsFiltered).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-2644">
                            <thead data-id="element-2645">
                              <tr className="bg-gray-100" data-id="element-2646">
                                <th className="border border-black p-2 text-left" data-id="element-2647">
                                  Parameter
                                </th>
                                <th className="border border-black p-2 text-left" data-id="element-2648">
                                  Result
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-2649">
                              {Object.entries(labResultsFiltered).map(([k, v]) => <tr key={k} data-id="element-2650">
                                    <td className="border border-black p-2 font-medium" data-id="element-2651">
                                      {k}
                                    </td>
                                    <td className="border border-black p-2" data-id="element-2652">
                                      {v}
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table> : <p className="text-gray-500 italic" data-id="element-2653">
                            No structured results recorded for this test.
                          </p>}

                        {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-2654">
                            <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-2655">
                              Comments / Observations / Interpretation
                            </h3>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-2656">
                              {lab.labComments}
                            </pre>
                          </div>}
                      </div>}

                    {rad && <div data-id="element-2657">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2658">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-2659">
                            <span className="font-semibold" data-id="element-2660">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-2661">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-2662">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-2663">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-2664">
                              <h3 className="font-bold" data-id="element-2665">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-2666">
                                <button onClick={() => setTestImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-2667">
                                  <ZoomOut className="w-4 h-4" data-id="element-2668" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={testImageZoom} onChange={e => setTestImageZoom(Number(e.target.value))} className="w-32" data-id="element-2669" />
                                <button onClick={() => setTestImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-2670">
                                  <ZoomIn className="w-4 h-4" data-id="element-2671" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-2672">
                                  {testImageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-2673">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-2674">
                                  <div style={{
                        transform: `scale(${testImageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-2675">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-2676" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  return <div className="space-y-6" data-id="element-2677">
      <div className="flex justify-between items-center" data-id="element-2678">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2679">
          Clinician Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-2680">
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-2681">
            {pendingEncounters.length} Patients Waiting
          </Badge>
          <Badge variant="success" className="text-sm px-3 py-1 bg-green-100 text-green-800" data-id="element-2682">
            {completedEncounters.length} Completed
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-2683">
        <nav className="-mb-px flex space-x-8" data-id="element-2684">
          <button onClick={() => setActiveTab('queue')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'queue' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2685">
            Active Queue
          </button>
          <button onClick={() => setActiveTab('completed')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'completed' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2686">
            Completed Consultations / History
          </button>
        </nav>
      </div>

      {activeTab === 'queue' && <Card noPadding data-id="element-2687">
          <ul className="divide-y divide-gray-200" data-id="element-2688">
            {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isInvestigations = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isInvestigations && !encounter.pendingLab && !encounter.pendingRadiology;
          const hasDraft = !!encounter.clinicalNote;
          const isPharmacy = encounter.stage === 'PHARMACY';
          const iconBg = hasResults ? 'bg-green-100' : isPharmacy ? 'bg-pink-100' : isInvestigations ? 'bg-yellow-100' : 'bg-blue-100';
          const iconColor = hasResults ? 'text-green-600' : isPharmacy ? 'text-pink-600' : isInvestigations ? 'text-yellow-600' : 'text-blue-600';
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2689">
                    <div className="flex items-center" data-id="element-2690">
                      <div className={`p-2 rounded-full mr-4 ${iconBg}`} data-id="element-2691">
                        <Stethoscope className={`h-6 w-6 ${iconColor}`} data-id="element-2692" />
                      </div>
                      <div data-id="element-2693">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2694">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2695">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1" data-id="element-2696">
                          <Badge variant="default" data-id="element-2697">{patient.age} yrs</Badge>
                          {hasResults && <Badge variant="success" data-id="element-2698">Results Ready</Badge>}
                          {isInvestigations && encounter.pendingLab && <Badge variant="warning" data-id="element-2699">Awaiting Lab</Badge>}
                          {isInvestigations && encounter.pendingRadiology && <Badge variant="warning" data-id="element-2700">Awaiting Radiology</Badge>}
                          {isPharmacy && <Badge variant="purple" data-id="element-2701">At Pharmacy</Badge>}
                          {hasDraft && encounter.stage === 'CLINICIAN' && <Badge variant="info" data-id="element-2702">Draft Saved</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2703">
                      Open File
                    </Button>
                  </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-2704">
                <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-2705" />
                <p className="text-lg font-medium text-gray-900" data-id="element-2706">
                  No patients waiting
                </p>
              </li>}
          </ul>
        </Card>}

      {activeTab === 'completed' && <Card data-id="element-2707">
          <div className="mb-4" data-id="element-2708">
            <input type="text" placeholder="Search completed consultations by patient name or file number..." className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2709" />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2710">
            <ul className="divide-y divide-gray-200" data-id="element-2711">
              {filteredCompleted.length > 0 ? filteredCompleted.map(encounter => {
            const patient = getPatient(encounter.patientId);
            if (!patient) return null;
            return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2712">
                      <div className="flex items-center" data-id="element-2713">
                        <div className="p-2 rounded-full mr-4 bg-green-100" data-id="element-2714">
                          <CheckCircle className="h-6 w-6 text-green-600" data-id="element-2715" />
                        </div>
                        <div data-id="element-2716">
                          <p className="text-sm font-medium text-blue-600" data-id="element-2717">
                            {patient.bmcFileNumber}
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-2718">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500" data-id="element-2719">
                            <span data-id="element-2720">
                              {new Date(encounter.startedAt).toLocaleDateString()}
                            </span>
                            <span data-id="element-2721">•</span>
                            <span data-id="element-2722">
                              Attended by:{' '}
                              {encounter.lastAttendingClinician || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2723">
                        Open File
                      </Button>
                    </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-2724">
                  <p className="text-lg font-medium text-gray-900" data-id="element-2725">
                    No completed consultations found
                  </p>
                </li>}
            </ul>
          </div>
        </Card>}
    </div>;
};